import * as React from "react";

import { useHasOrgRole } from "@/providers/auth/hooks";
import { IngestionStatus, type FileRecord } from "@/shared/domain/files";
import type { IngestionRun } from "@/shared/domain/ingestionRules";

/** What a `LatestIngestionRunsProvider` knows about one file's latest run. */
export interface ProvidedRun {
  /** Null when the file has none. */
  run: IngestionRun | null;
  /**
   * The file record was read before the run was seen to end; see
   * `IngestionLabelContext.recordPredatesRunEnd`.
   */
  recordPredatesRunEnd: boolean;
}

/** A file's latest run; undefined when not looked up. */
export type RunLookup = (fileId: string) => ProvidedRun | undefined;

export const LatestRunsContext = React.createContext<RunLookup | null>(null);

/**
 * Only an ingestable file not yet fully ingested can have a run worth showing:
 * an ingestion rule's trigger runs only on files it marked ingestable.
 */
export function mayHaveRun(
  file: Pick<FileRecord, "ingestion_status" | "ingestable">,
): boolean {
  return (
    file.ingestable === true &&
    file.ingestion_status !== IngestionStatus.Ingested
  );
}

/**
 * How long after upload a file with no run may still get one from its rule's
 * trigger. Until then its badge keeps checking for the run, and does not offer
 * to ingest the file.
 */
export const FIRST_RUN_GRACE_MS = 10 * 60 * 1000;

/**
 * For each of `files`, when to stop expecting a first run on it, as
 * `useLatestIngestionRuns` takes it. A file with no upload time gets none.
 */
export function firstRunDeadlines(
  files: readonly (Pick<FileRecord, "file_id"> &
    Partial<Pick<FileRecord, "created">>)[],
): Record<string, number> {
  return Object.fromEntries(
    files.flatMap((file) =>
      file.created === undefined
        ? []
        : [[file.file_id, Date.parse(file.created) + FIRST_RUN_GRACE_MS]],
    ),
  );
}

/**
 * Whether the viewer belongs to `orgId` (the current org when omitted). The
 * ingestion-rule endpoints refuse anyone else, such as a viewer of another
 * org's public dataset, so ask them only when this holds.
 */
export function useIsOrgMember(orgId: string | undefined): boolean {
  return useHasOrgRole("user", { orgId });
}

/**
 * A file's latest run as the nearest `LatestIngestionRunsProvider` knows it;
 * undefined without one, or before its request answers.
 */
export function useProvidedIngestionRun(
  fileId: string | undefined,
): ProvidedRun | undefined {
  const lookup = React.useContext(LatestRunsContext);
  return fileId === undefined ? undefined : lookup?.(fileId);
}
